import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAdminRenewSubscriptionMutation } from "../../../services/organization/subscription";
import Spinner from "../../Spinner";
import { useCurrency } from "../../../hooks/useCurrency";

/**
 * ManageSubscriptionModal Global popup for expired/cancelled subscriptions.
 */
const ManageSubscriptionModal = ({ isOpened, onClose, subscription, upgradePlans, onSuccess }) => {
  const { t, i18n } = useTranslation("subscription");
  const currentLanguage = i18n.language;

  const [renewSubscription, { isLoading: isRenewing }] = useAdminRenewSubscriptionMutation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { convertAndFormat } = useCurrency();

  const isBusy = isRenewing || isRedirecting;

  if (!isOpened) return null;

  const plan = subscription?.plan || {};
  const renewalPricing = subscription?.renewal_pricing || {};
  const currency = subscription?.currency || "KWD";

  const formatMoney = (amount) => {
    if (amount == null || amount === "") return "-";
    return convertAndFormat(amount);
  };

  const handleRenew = async () => {
    if (isBusy) return;
    try {
      const response = await renewSubscription({}).unwrap();
      const redirectUrl = response?.data?.redirect_url;

      if (redirectUrl) {
        setIsRedirecting(true);
        // 3DS redirect required
        window.location.href = redirectUrl;
      } else {
        // Immediate success
        onSuccess?.(response?.data?.status);
        onClose();
      }
    } catch (error) {
      const msg =
        error?.data?.message ||
        error?.message ||
        t("retryPayment.failed", "Payment failed. Please try a different card.");
      toast.error(msg);
    }
  };

  return (
    <>
      <div
        className="modal main-modal manage-sub-modal fade show"
        id="ManageSubscriptionModal"
        style={{ display: "block" }}
        tabIndex="-1"
        aria-labelledby="ManageSubModalLabel"
        aria-modal="true"
        role="dialog"
      >
        {/* Backdrop */}
        <div
          className="modal-backdrop-overlay"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="modal-dialog modal-dialog-centered manage-sub-modal__dialog">
          <div className="modal-content manage-sub-modal__content">
            {/* Left Panel */}
            <div className="manage-sub-modal__left">
              <div className="logo-wrapper">
                <img
                  src={"/images/business-logo-dark.svg"}
                  alt="Tadarab"
                  className="manage-sub-modal__logo"
                />
                <button
                  type="button"
                  className="manage-sub-modal__close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="manage-sub-modal__left-body">
                <h2 className="manage-sub-modal__headline">
                  {t(
                    "manageSubscription.headline",
                    "Your Subscription is Expired.",
                  )}
                </h2>
                <p className="manage-sub-modal__lead">
                  {t(
                    "manageSubscription.lead",
                    "Access to your premium architectural tools and workspace has been paused. Renew now to restore your progress.",
                  )}
                </p>
              </div>
            </div>

            {/* Right Panel */}
            <div className="manage-sub-modal__right">
              {/* Close button */}
              <button
                type="button"
                className="manage-sub-modal__close"
                onClick={onClose}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <h3 id="ManageSubModalLabel" className="manage-sub-modal__right-title">
                {t("manageSubscription.manageTitle", "Manage your subscription")}
              </h3>
              <p className="manage-sub-modal__right-subtitle">
                {t(
                  "manageSubscription.manageSubtitle",
                  "Renew your current plan or upgrade for more features.",
                )}
              </p>

              {/* Current Plan */}
              <p className="manage-sub-modal__section-label">
                {t("manageSubscription.currentPlan", "Your Current Plan")}
              </p>
              <div className="manage-sub-modal__plan-card manage-sub-modal__plan-card--current">
                <div className="card-left">
                  <div className="manage-sub-modal__plan-card-header">
                    <div className="manage-sub-modal__plan-card-name-row">
                      <span className="manage-sub-modal__plan-name">
                        {plan?.title || t("manageSubscription.unknownPlan", "Current Plan")}
                      </span>
                      <span className="manage-sub-modal__badge manage-sub-modal__badge--expired">
                        {t(`status.${subscription?.status}`, t("manageSubscription.expired", "Expired"))}
                      </span>
                    </div>
                  </div>
                  <p className="manage-sub-modal__plan-desc">
                    {plan?.subtitle || ""}
                  </p>
                  <button
                    type="button"
                    className="manage-sub-modal__renew-btn"
                    onClick={handleRenew}
                    disabled={isBusy}
                  >
                    {isBusy && (
                      <Spinner
                        className="spinner-border-sm me-2"
                        style={{
                          width: "1rem",
                          height: "1rem",
                          marginRight: "0.5rem",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      />
                    )}
                    {isBusy
                      ? t("manageSubscription.processing", "Processing...")
                      : t("manageSubscription.renewPlan", "Renew Plan")}
                  </button>
                </div>
                <div className="card-right">
                  <span className="manage-sub-modal__plan-price">
                    <strong>{formatMoney(renewalPricing?.total_amount)}</strong>
                    <small>/{t(subscription?.cadence) || t("month", "mo")}</small>
                  </span>
                </div>
              </div>

              {/* Upgrade Options */}
              {upgradePlans && upgradePlans.length > 0 && (
                <>
                  <p className="manage-sub-modal__section-label manage-sub-modal__section-label--mt">
                    {t("manageSubscription.upgradeOptions", "Upgrade Options")}
                  </p>

                  <div className="manage-sub-modal__upgrade-list">
                    {upgradePlans.map((plan) => (
                      <div
                        key={plan.id}
                        className="manage-sub-modal__plan-card manage-sub-modal__plan-card--upgrade"
                      >
                        <div className="card-left">
                          <div className="manage-sub-modal__plan-card-header">
                            <div className="manage-sub-modal__plan-card-name-row">
                              <span className="manage-sub-modal__plan-name">
                                {plan.title}
                              </span>
                            </div>
                          </div>
                          <p className="manage-sub-modal__plan-desc">{plan?.subtitle}</p>
                          <button
                            type="button"
                            className="manage-sub-modal__upgrade-btn"
                            disabled
                          >
                            {t("manageSubscription.upgradeNow", "Upgrade Now")}
                          </button>
                        </div>
                        <div className="card-right">
                          <span className="manage-sub-modal__plan-price">
                            <strong>
                              {plan.plan_type === "standard" && plan.pricing_model === "per_seat"
                                ? convertAndFormat(plan.price_per_seat)
                                : convertAndFormat(plan.base_price)}
                            </strong>
                            <small>/{t(plan?.cadence) || plan?.cadence}</small>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Trust line */}
              <p className="manage-sub-modal__trust-line">
                {t(
                  "manageSubscription.trustLine",
                  "Payments are secure and encrypted. Cancel any time. By continuing, you agree to our",
                )}{" "}
                <a href="/terms" className="manage-sub-modal__terms-link">
                  {t("manageSubscription.termsOfService", "Terms of Service")}
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ManageSubscriptionModal.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  upgradePlans: PropTypes.array,
  onSuccess: PropTypes.func,
};

ManageSubscriptionModal.defaultProps = {
  upgradePlans: [],
};

export default ManageSubscriptionModal;
